import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Layout, Typography, Button, Card, Row, Col, Input, Select, 
  DatePicker, Space, Tag, Empty, Spin 
} from 'antd';
import {
  SearchOutlined, FilterOutlined, DownloadOutlined, EyeOutlined,
  CalendarOutlined, ReloadOutlined, GlobalOutlined, HomeOutlined,
  DownOutlined
} from '@ant-design/icons';
import { getPolicies } from '../api/policyApi';
import moment from 'moment';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const CentGovPolicy = () => {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredPolicyId, setHoveredPolicyId] = useState(null);
  
  const [filters, setFilters] = useState({
    search: '',
    dateRange: null,
    sector: '',
    state: ''
  });
  const [sectorOptions, setSectorOptions] = useState([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Fetch policies from the database
  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const response = await getPolicies();
      if (response && response.data) {
        const allData = Array.isArray(response.data) ? response.data : [];
        // Filter to only show Central Government policies
        const policiesOnly = allData.filter(item => item.type === 'central-government-policy');
        console.log('Fetched policies:', policiesOnly);
        console.log('First policy data:', policiesOnly[0]);
        setPolicies(policiesOnly);

        const uniqueSectors = Array.from(
          new Set(policiesOnly.map(p => p.sector).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
        setSectorOptions(uniqueSectors);
      }
    } catch (error) {
      console.error('Error fetching policies:', error);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    fetchFilteredPolicies();
  };

  const handleReset = () => {
    setFilters({
      search: '',
      dateRange: null,
      sector: '',
      state: ''
    });
    fetchPolicies();
  };

  const fetchFilteredPolicies = async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (filters.search) params.search = filters.search;
      if (filters.sector) params.sector = filters.sector;
      if (filters.state) params.state = filters.state;
      
      const response = await getPolicies(params);
      if (response && response.data) {
        let filteredData = Array.isArray(response.data) ? response.data : [];
        
        // Filter to only show Central Government policies
        filteredData = filteredData.filter(item => item.type === 'central-government-policy');
        
        // Filter by date range if provided
        if (filters.dateRange && filters.dateRange.length === 2) {
          const [startDate, endDate] = filters.dateRange;
          filteredData = filteredData.filter(policy => {
            const policyDate = moment(policy.releaseDate);
            return policyDate.isBetween(startDate, endDate, 'day', '[]');
          });
        }
        
        setPolicies(filteredData);
      }
    } catch (error) {
      console.error('Error fetching filtered policies:', error);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('MMM DD, YYYY');
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#fafbfc', fontFamily: "'Rubik', sans-serif" }}>
      <style>{`
        @media (max-width: 768px) {
          .cgp-content {
            padding: 24px 16px 60px !important;
          }
          .cgp-title {
            font-size: 30px !important;
            margin-bottom: 14px !important;
          }
          .cgp-subtitle {
            font-size: 15px !important;
          }
          .cgp-cards-grid {
            grid-template-columns: 1fr !important;
          }
          .cgp-filters-toggle {
            display: flex !important;
          }
          .cgp-filters-body {
            display: none;
          }
          .cgp-filters-body.cgp-filters-open {
            display: block;
            margin-top: 16px;
          }
          .cgp-filters-card {
            margin-bottom: 24px !important;
            padding: 0 16px !important;
          }
          .cgp-filters-card .ant-card-body {
            padding: 12px 0 !important;
          }
          .cgp-filters-body,
          .cgp-filters-body * {
            text-align: left !important;
          }
        }
        .cgp-filters-toggle {
          display: none;
        }
      `}</style>
      <Content className="cgp-content" style={{ padding: '50px 48px 80px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <Title level={1} className="cgp-title" style={{
              margin: 0,
              color: '#000000',
              fontFamily: "'Rubik', sans-serif",
              fontSize: 46,
              fontWeight: 600,
              marginBottom: 24,
              letterSpacing: '-0.5px'
            }}>
              Central Government Policy
            </Title>
            <Text className="cgp-subtitle" style={{
              fontSize: 18,
              color: '#666',
              fontFamily: "'Rubik', sans-serif",
              lineHeight: '1.8',
              fontWeight: 400,
              maxWidth: 850,
              margin: '0 auto',
              display: 'block'
            }}>
              Explore Central government policies and strategic recommendations shaping India's industrial landscape
            </Text>
          </div>

          {/* Search and Filters Card */}
          <Card
            bordered={false}
            className="cgp-filters-card"
            style={{
              background: '#fff',
              borderRadius: 12,
              marginBottom: 48,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: '1px solid #eee',
              margin: '0 auto 48px',
              padding: '16px 24px'
            }}
          >
            <button
              type="button"
              className="cgp-filters-toggle"
              onClick={() => setMobileFiltersOpen((prev) => !prev)}
              style={{
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                padding: '12px 16px',
                background: '#f9f3fc',
                border: '1px solid #e6d5f0',
                borderRadius: 8,
                cursor: 'pointer',
                fontFamily: "'Rubik', sans-serif",
                fontSize: 15,
                fontWeight: 600,
                color: '#470172'
              }}
            >
              <FilterOutlined style={{ fontSize: 15 }} />
              <span>{mobileFiltersOpen ? 'Hide Filters' : 'Search & Filter Policies'}</span>
              <DownOutlined
                style={{
                  fontSize: 13,
                  marginLeft: 4,
                  transform: mobileFiltersOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}
              />
            </button>
            <div className={`cgp-filters-body ${mobileFiltersOpen ? 'cgp-filters-open' : ''}`}>
            <Row gutter={[16, 8]}>
              <Col xs={24} lg={12}>
                <Text strong style={{ 
                  display: 'block', 
                  marginBottom: 6, 
                  fontFamily: "'Rubik', sans-serif", 
                  fontSize: 14,
                  color: '#262626'
                }}>
                  Search Policies
                </Text>
                <Input
                  size="middle"
                  placeholder="Search policy recommendations..."
                  prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  style={{ fontFamily: "'Rubik', sans-serif" }}
                />
              </Col>
              <Col xs={24} lg={12}>
                <Text strong style={{ 
                  display: 'block', 
                  marginBottom: 6, 
                  fontFamily: "'Rubik', sans-serif", 
                  fontSize: 14,
                  color: '#262626'
                }}>
                  Date Range
                </Text>
                <RangePicker
                  size="middle"
                  style={{ width: '100%', fontFamily: "'Rubik', sans-serif" }}
                  value={filters.dateRange}
                  onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
                  format="MMM DD, YYYY"
                />
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Text strong style={{ 
                  display: 'block', 
                  marginBottom: 6, 
                  fontFamily: "'Rubik', sans-serif", 
                  fontSize: 14,
                  color: '#262626'
                }}>
                  Sector
                </Text>
                <Select
                  size="middle"
                  value={filters.sector}
                  onChange={(value) => setFilters(prev => ({ ...prev, sector: value }))}
                  placeholder="Select Sector"
                  style={{ width: '100%', fontFamily: "'Rubik', sans-serif" }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {sectorOptions.map((sector) => (
                    <Option key={sector} value={sector}>{sector}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Text strong style={{ 
                  display: 'block', 
                  marginBottom: 6, 
                  fontFamily: "'Rubik', sans-serif", 
                  fontSize: 14,
                  color: '#262626'
                }}>
                  State
                </Text>
                <Select
                  size="middle"
                  value={filters.state}
                  onChange={(value) => setFilters(prev => ({ ...prev, state: value }))}
                  placeholder="Select State"
                  style={{ width: '100%', fontFamily: "'Rubik', sans-serif" }}
                  allowClear
                >
                  <Option value="Maharashtra">Maharashtra</Option>
                </Select>
              </Col>
              <Col xs={24} md={24} lg={8}>
                <Text strong style={{ 
                  display: 'block', 
                  marginBottom: 6, 
                  fontFamily: "'Rubik', sans-serif", 
                  fontSize: 14,
                  color: '#262626',
                  visibility: 'hidden'
                }}>
                  Actions
                </Text>
                <Space style={{ width: '100%' }}>
                  <Button
                    type="primary"
                    size="middle"
                    icon={<SearchOutlined />}
                    onClick={handleSubmit}
                    loading={loading}
                    style={{
                      fontFamily: "'Rubik', sans-serif",
                      fontSize: 14,
                      backgroundColor: '#470172',
                      borderColor: '#470172',
                      flex: 1
                    }}
                  >
                    Submit
                  </Button>
                  <Button
                    size="middle"
                    icon={<FilterOutlined />}
                    onClick={handleReset}
                    style={{
                      fontFamily: "'Rubik', sans-serif",
                      fontSize: 14,
                      color: '#470172',
                      borderColor: '#d9d9d9',
                      flex: 1
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    size="middle"
                    icon={<ReloadOutlined spin={loading} />}
                    onClick={fetchPolicies}
                    disabled={loading}
                    style={{
                      fontFamily: "'Rubik', sans-serif",
                      fontSize: 14,
                      color: '#470172',
                      borderColor: '#d9d9d9'
                    }}
                  />
                </Space>
              </Col>
            </Row>
            </div>
          </Card>

          {/* Policies List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <Spin size="large" />
              <p style={{ marginTop: 24, color: '#666', fontFamily: "'Rubik', sans-serif", fontSize: 16 }}>Loading policies...</p>
            </div>
          ) : policies.length === 0 ? (
            <Card bordered={false} style={{ background: '#fff', borderRadius: 12, border: '1px solid #eee' }}>
              <Empty
                description={
                  <span style={{ fontFamily: "'Rubik', sans-serif", fontSize: 18, fontWeight: 600, color: '#470172' }}>
                    No policies found. Try adjusting your filters.
                  </span>
                }
              />
            </Card>
          ) : (
            <div className="cgp-cards-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 30 }}>
              {policies.map((policy) => (
                <div
                  key={policy._id}
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: 12,
                    overflow: 'hidden',
                    boxShadow: hoveredPolicyId === policy._id ? '0 20px 40px rgba(0,0,0,0.12)' : '0 4px 10px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    border: '1px solid #eee',
                    transform: hoveredPolicyId === policy._id ? 'translateY(-8px)' : 'translateY(0)',
                    height: '100%'
                  }}
                  onMouseEnter={() => setHoveredPolicyId(policy._id)}
                  onMouseLeave={() => setHoveredPolicyId(null)}
                >
                  {/* Content Section */}
                  <div style={{ padding: 24, display: 'flex', flexDirection: 'column', flex: 1 }}>
                    {/* Meta Info and Tags Row */}
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: 12,
                      gap: 12
                    }}>
                      {/* Date and Sector */}
                      <div style={{ 
                        fontSize: 13, 
                        color: '#470172', 
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap'
                      }}>
                        <CalendarOutlined />
                        <span>{formatDate(policy.releaseDate || policy.createdAt)}</span>
                        {policy.sector && (
                          <>
                            <span style={{ color: '#ccc' }}>•</span>
                            <span style={{ color: '#999' }}>{policy.sector}</span>
                          </>
                        )}
                      </div>

                      {/* Tags */}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Tag
                          style={{
                            fontFamily: "'Rubik', sans-serif",
                            fontSize: 12,
                            padding: '4px 12px',
                            borderRadius: 6,
                            border: 'none',
                            background: '#f3e5ff',
                            color: '#470172',
                            margin: 0
                          }}
                        >
                          Policy
                        </Tag>
                        {policy.isInternational !== undefined && (
                          <Tag
                            icon={policy.isInternational ? <GlobalOutlined /> : <HomeOutlined />}
                            style={{
                              fontFamily: "'Rubik', sans-serif",
                              fontSize: 12,
                              fontWeight: 500,
                              padding: '4px 12px',
                              borderRadius: 6,
                              border: 'none',
                              background: policy.isInternational ? '#e6f7ff' : '#fff7e6',
                              color: policy.isInternational ? '#1890ff' : '#fa8c16',
                              margin: 0
                            }}
                          >
                            {policy.isInternational ? 'International' : 'National'}
                          </Tag>
                        )}
                      </div>
                    </div>

                    {/* Title */}
                    <h3 style={{
                      fontSize: 19,
                      fontWeight: 600,
                      color: hoveredPolicyId === policy._id ? '#470172' : '#2d3748',
                      marginBottom: 12,
                      lineHeight: 1.4,
                      transition: 'color 0.3s ease',
                      fontFamily: "'Rubik', sans-serif",
                      margin: 0,
                      marginBottom: 12
                    }}>
                      {policy.title}
                    </h3>

                    {/* Description */}
                    {policy.description && (
                      <p style={{
                        fontSize: 15,
                        color: '#718096',
                        lineHeight: 1.6,
                        marginBottom: 20,
                        fontFamily: "'Rubik', sans-serif",
                        flex: 1
                      }}>
                        {policy.description.substring(0, 150)}{policy.description.length > 150 ? '...' : ''}
                      </p>
                    )}

                    {/* Action Buttons */}
                    <div style={{ 
                      marginTop: 'auto', 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      gap: 12 
                    }}>
                      <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={() => {
                          // Check if policy has a file (either has fileId, fileName, or just exists as a document)
                          const hasFile = policy.fileId || policy.fileName || policy._id;
                          if (hasFile) {
                            console.log('Opening file for policy:', policy._id, 'fileId:', policy.fileId, 'fileName:', policy.fileName);
                            window.open(`${process.env.REACT_APP_API_BASE_URL || ''}/api/policies/${policy._id}/download?inline=true`, '_blank');
                          }
                        }}
                        disabled={!policy._id}
                        style={{
                          fontFamily: "'Rubik', sans-serif",
                          fontSize: 14,
                          backgroundColor: policy._id ? '#470172' : '#d9d9d9',
                          borderColor: policy._id ? '#470172' : '#d9d9d9',
                          fontWeight: 500,
                          height: 36,
                          flex: 1
                        }}
                      >
                        View
                      </Button>
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={() => {
                          // Check if policy has a file (either has fileId, fileName, or just exists as a document)
                          const hasFile = policy.fileId || policy.fileName || policy._id;
                          if (hasFile) {
                            console.log('Downloading file for policy:', policy._id);
                            const link = document.createElement('a');
                            link.href = `${process.env.REACT_APP_API_BASE_URL || ''}/api/policies/${policy._id}/download`;
                            link.download = `${policy.title}.${policy.fileType || 'pdf'}`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }
                        }}
                        disabled={!policy._id}
                        style={{
                          fontFamily: "'Rubik', sans-serif",
                          fontSize: 14,
                          color: policy._id ? '#470172' : '#999',
                          borderColor: policy._id ? '#470172' : '#d9d9d9',
                          fontWeight: 500,
                          height: 36,
                          flex: 1
                        }}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </Content>
    </Layout>
  );
};

export default CentGovPolicy;
